/**
 * Every API route, with the roles allowed to call it (AUT-030). The web app uses this table to
 * build requests, and the Worker must supply a handler for each id, which the type of its handler
 * map enforces. A test walks the table so a route with no roles, or a clinical route open to
 * anyone, cannot be added unnoticed.
 *
 * `anyone` means no session is needed. `signed_in` means any session, including an anonymous one
 * started by an upload.
 *
 * Staff routes live under /api/admin and list staff roles only. In staging and production the
 * Worker also refuses them, and staff sign-in under /api/staff, unless Cloudflare Access signed the
 * request in (AUT-031). The Access application covers the admin hostname and the /api/admin/* and
 * /api/staff/* paths on the API hostname, and with REQUIRE_ACCESS "on" a request without Access's
 * `Cf-Access-Authenticated-User-Email` header is refused before any session is read. A staff role
 * or an Access sign-in alone is not enough.
 *
 * Who holds which staff route:
 *   search, user, funnel   superuser, clinician, operations, support. A clinician looks up the
 *                          patient a review is about. The funnel is who signed up and where they
 *                          are in onboarding.
 *   organizations          superuser, operations, support.
 *   lock, unlock, links    superuser and support only. A lock stops a customer signing in and
 *                          moving a link changes which clinic sees a record, so these are account
 *                          decisions, not operations or clinical ones.
 *   a patient's record     superuser, clinician, operations, support, through canReadPatient, which
 *                          asks for a reason outside a clinician's queue (ADM-004).
 *   reviews                superuser and clinician. The queue and the approve / edit / decline
 *                          screen (ADM-020, ADM-021, CLI-001 to CLI-007).
 *   jobs                   superuser, clinician, operations, support. Extraction runs and formula
 *                          drafts, with the model, prompt and rule set each job used.
 *   retry an extraction    superuser, operations, support. Re-queues a failed document. A clinician
 *                          can see the failure; they do not rerun the reader.
 *   pharmacy orders        superuser, operations, support. List, manual status (FUL-020), resubmit,
 *                          cancel, and confirming a portal task (ADM-040).
 *   billing                superuser, operations, support. Subscription, charges, refund, pause
 *                          and cancel on the customer's behalf (ADM-041).
 * Engine staff hold none of them. They work on de-identified evaluation cases and never see a name,
 * an email or a record (PRI-003).
 */
import type { ClinicRole, StaffRole } from "./access.ts";

export type Role = "anyone" | "signed_in" | "customer" | ClinicRole | StaffRole;

export interface RouteSpec {
  readonly method: "GET" | "POST" | "PUT" | "DELETE";
  /** Path with `:name` parameters, always under /api. */
  readonly path: `/api/${string}`;
  readonly roles: readonly Role[];
  /** True when the route returns or changes a patient's clinical record. */
  readonly clinical: boolean;
}

const ACCOUNT_READERS = ["superuser", "clinician", "operations", "support"] as const;

const ACCOUNT_CHANGERS = ["superuser", "support"] as const;

const REVIEWERS = ["superuser", "clinician"] as const;

const OPS = ["superuser", "operations", "support"] as const;

const ENGINE_STAFF = ["superuser", "engine"] as const;

export const ROUTES = {
  health: { method: "GET", path: "/api/health", roles: ["anyone"], clinical: false },
  /* Better Auth's own endpoints (sessions, magic link verification, sign-out). It checks them itself. */
  authGet: { method: "GET", path: "/api/auth/*", roles: ["anyone"], clinical: false },
  authPost: { method: "POST", path: "/api/auth/*", roles: ["anyone"], clinical: false },
  /* Email entry during onboarding: Turnstile, the hourly limit, then a magic link (AUT-002, AUT-010). */
  sessionEmail: {
    method: "POST",
    path: "/api/session/email",
    roles: ["signed_in"],
    clinical: false,
  },
  /* Lab files. Anonymous visitors count as customers, since starting a session gives them a patient. */
  uploads: { method: "POST", path: "/api/uploads", roles: ["customer"], clinical: true },
  /* Claiming a record a clinic created, by its invitation token (AUT-020). */
  claim: { method: "POST", path: "/api/claims", roles: ["customer"], clinical: true },
  /* The customer's saved answers, so the intake resumes where they left off (INT-004). */
  intake: { method: "GET", path: "/api/intake", roles: ["customer"], clinical: true },
  /* One intake answer, checked against its question before it is stored (INT-031). */
  intakeAnswer: {
    method: "PUT",
    path: "/api/intake/:questionId",
    roles: ["customer"],
    clinical: true,
  },
  /* Draft the customer's formula from their answers and labs, and send it to a physician. */
  draftFormula: {
    method: "POST",
    path: "/api/formulas/draft",
    roles: ["customer"],
    clinical: true,
  },
  /* The customer's in-review formula, or `{ formula: null }` when none has been drafted yet. */
  currentFormula: {
    method: "GET",
    path: "/api/formulas/current",
    roles: ["customer"],
    clinical: true,
  },
  /* The customer's current formula and every past version, with what changed (ACC-001, ACC-002). */
  accountFormulas: {
    method: "GET",
    path: "/api/account/formulas",
    roles: ["customer"],
    clinical: true,
  },
  accountClinics: {
    method: "GET",
    path: "/api/account/clinics",
    roles: ["customer"],
    clinical: true,
  },
  accountRevokeClinic: {
    method: "POST",
    path: "/api/account/clinics/:linkId/revoke",
    roles: ["customer"],
    clinical: true,
  },
  accountShareClinic: {
    method: "POST",
    path: "/api/account/clinics/:linkId/share",
    roles: ["customer"],
    clinical: true,
  },
  accountExport: {
    method: "GET",
    path: "/api/account/export",
    roles: ["customer"],
    clinical: true,
  },
  accountDeletion: {
    method: "GET",
    path: "/api/account/deletion",
    roles: ["customer"],
    clinical: true,
  },
  accountRequestDeletion: {
    method: "POST",
    path: "/api/account/deletion",
    roles: ["customer"],
    clinical: true,
  },
  accountOrders: {
    method: "GET",
    path: "/api/account/orders",
    roles: ["customer"],
    clinical: true,
  },
  accountLabs: {
    method: "GET",
    path: "/api/account/labs",
    roles: ["customer"],
    clinical: true,
  },
  accountLabFile: {
    method: "GET",
    path: "/api/account/labs/:documentId/file",
    roles: ["customer"],
    clinical: true,
  },
  accountFormula: {
    method: "GET",
    path: "/api/account/formula",
    roles: ["customer"],
    clinical: true,
  },
  accountFormulaRemove: {
    method: "POST",
    path: "/api/account/formula/remove",
    roles: ["customer"],
    clinical: true,
  },
  accountFormulaRestore: {
    method: "POST",
    path: "/api/account/formula/restore",
    roles: ["customer"],
    clinical: true,
  },
  accountFormulaOptions: {
    method: "POST",
    path: "/api/account/formula/options",
    roles: ["customer"],
    clinical: true,
  },
  accountSubscription: {
    method: "GET",
    path: "/api/account/subscription",
    roles: ["customer"],
    clinical: false,
  },
  accountSubscriptionPause: {
    method: "POST",
    path: "/api/account/subscription/pause",
    roles: ["customer"],
    clinical: false,
  },
  accountSubscriptionSkip: {
    method: "POST",
    path: "/api/account/subscription/skip",
    roles: ["customer"],
    clinical: false,
  },
  accountSubscriptionMove: {
    method: "POST",
    path: "/api/account/subscription/move",
    roles: ["customer"],
    clinical: false,
  },
  accountSubscriptionCancel: {
    method: "POST",
    path: "/api/account/subscription/cancel",
    roles: ["customer"],
    clinical: false,
  },
  accountSubscriptionRestart: {
    method: "POST",
    path: "/api/account/subscription/restart",
    roles: ["customer"],
    clinical: false,
  },
  accountSubscriptionPortal: {
    method: "POST",
    path: "/api/account/subscription/portal",
    roles: ["customer"],
    clinical: false,
  },
  checkout: { method: "GET", path: "/api/checkout", roles: ["customer"], clinical: true },
  checkoutRemove: {
    method: "POST",
    path: "/api/checkout/remove",
    roles: ["customer"],
    clinical: true,
  },
  checkoutRestore: {
    method: "POST",
    path: "/api/checkout/restore",
    roles: ["customer"],
    clinical: true,
  },
  checkoutOptions: {
    method: "POST",
    path: "/api/checkout/options",
    roles: ["customer"],
    clinical: true,
  },
  checkoutAddress: {
    method: "PUT",
    path: "/api/checkout/address",
    roles: ["customer"],
    clinical: false,
  },
  checkoutPay: {
    method: "POST",
    path: "/api/checkout/pay",
    roles: ["customer"],
    clinical: true,
  },
  /*
    Stripe signs this itself (PAY-011). Anyone is correct: the caller is Stripe, not a session.
    Card numbers never appear here, only event ids and Stripe object ids (PAY-031).
  */
  stripeWebhook: {
    method: "POST",
    path: "/api/stripe/webhook",
    roles: ["anyone"],
    clinical: false,
  },
  /* A sign-in link for an email, with no session needed. Turnstile and the hourly limit still apply. */
  signIn: { method: "POST", path: "/api/sign-in", roles: ["anyone"], clinical: false },
  /*
    The caller's own lab processing progress (LAB-006): a WebSocket upgrade that receives every
    change, and the same state as JSON for clients that cannot hold a socket. Neither takes a
    patient id, so a customer can only ever reach their own.
  */
  processing: { method: "GET", path: "/api/processing", roles: ["customer"], clinical: true },
  processingSnapshot: {
    method: "GET",
    path: "/api/processing/snapshot",
    roles: ["customer"],
    clinical: true,
  },
  me: { method: "GET", path: "/api/me", roles: ["signed_in"], clinical: false },
  patient: {
    method: "GET",
    path: "/api/patients/:patientId",
    roles: [
      "customer",
      "practitioner",
      "assistant",
      "superuser",
      "clinician",
      "operations",
      "support",
    ],
    clinical: true,
  },
  /*
    Open to anyone, since the caller is a mail client acting on the List-Unsubscribe header or the
    web page behind an email's footer link. The path token is signed, so only someone holding that
    email can use it (MSG-042).
  */
  unsubscribe: {
    method: "POST",
    path: "/api/email/unsubscribe/:token",
    roles: ["anyone"],
    clinical: false,
  },
  /*
    Open to anyone: the caller is following a signed one-click link from MSG-020. The path token
    names the check-in and the answer, so only someone holding that email can record it (MSG-045).
    GET does nothing; mail scanners must not count as a reply.
  */
  checkIn: {
    method: "POST",
    path: "/api/check-in/:token",
    roles: ["anyone"],
    clinical: false,
  },
  /*
    Staff ask for a magic link here rather than through Better Auth's own endpoint, which needs a
    Turnstile token. A link goes out only to an email that holds a staff role, and the answer is
    the same either way, so the route does not reveal which emails are staff. In production it sits
    behind Cloudflare Access with the rest of the admin surface.
  */
  staffSignIn: { method: "POST", path: "/api/staff/sign-in", roles: ["anyone"], clinical: false },
  /* ADM-001: users, patients and organizations by name, email, id or order number, in `?q=`. */
  adminSearch: {
    method: "GET",
    path: "/api/admin/search",
    roles: ACCOUNT_READERS,
    clinical: false,
  },
  /* Customers who signed up, the onboarding step each is in, and counts. `?stage=` filters the list. */
  adminFunnel: {
    method: "GET",
    path: "/api/admin/funnel",
    roles: ACCOUNT_READERS,
    clinical: false,
  },
  adminUser: {
    method: "GET",
    path: "/api/admin/users/:userId",
    roles: ACCOUNT_READERS,
    clinical: false,
  },
  /* ADM-002 and AUT-032. Both take `{ reason }`, and a lock ends every session the user has. */
  adminLock: {
    method: "POST",
    path: "/api/admin/users/:userId/lock",
    roles: ACCOUNT_CHANGERS,
    clinical: false,
  },
  adminUnlock: {
    method: "POST",
    path: "/api/admin/users/:userId/unlock",
    roles: ACCOUNT_CHANGERS,
    clinical: false,
  },
  /* ADM-003: members, roles and patient links. */
  adminOrganization: {
    method: "GET",
    path: "/api/admin/organizations/:organizationId",
    roles: ["superuser", "operations", "support"],
    clinical: false,
  },
  /* Takes `{ organizationId, reason }`. */
  adminMoveLink: {
    method: "POST",
    path: "/api/admin/patient-links/:linkId/move",
    roles: ACCOUNT_CHANGERS,
    clinical: false,
  },
  /* Takes `{ reason }`. The link is revoked rather than deleted, so its history stays. */
  adminRemoveLink: {
    method: "POST",
    path: "/api/admin/patient-links/:linkId/remove",
    roles: ACCOUNT_CHANGERS,
    clinical: false,
  },
  /* ADM-004. The reason travels in the `x-access-reason` header, and every read is audited. */
  adminPatient: {
    method: "GET",
    path: "/api/admin/patients/:patientId",
    roles: ACCOUNT_READERS,
    clinical: true,
  },
  /* ADM-020, CLI-001. Waiting and in-progress reviews, oldest first. */
  adminReviews: {
    method: "GET",
    path: "/api/admin/reviews",
    roles: REVIEWERS,
    clinical: true,
  },
  /* ADM-021, CLI-002 to CLI-004. The review screen payload. */
  adminReview: {
    method: "GET",
    path: "/api/admin/reviews/:reviewId",
    roles: REVIEWERS,
    clinical: true,
  },
  /* CLI-005, CLI-006. Optional `{ edits }`; each edit needs a review_edit reason code. */
  adminApproveReview: {
    method: "POST",
    path: "/api/admin/reviews/:reviewId/approve",
    roles: REVIEWERS,
    clinical: true,
  },
  /* CLI-007. Body `{ reason, note }` is required. */
  adminDeclineReview: {
    method: "POST",
    path: "/api/admin/reviews/:reviewId/decline",
    roles: REVIEWERS,
    clinical: true,
  },
  /* Extraction runs and engine drafts, plus the live model, prompt and rule set. */
  adminJobs: {
    method: "GET",
    path: "/api/admin/jobs",
    roles: ACCOUNT_READERS,
    clinical: false,
  },
  /* Takes `{ reason }`. Re-queues a failed document so the extraction consumer reads it again. */
  adminRetryExtraction: {
    method: "POST",
    path: "/api/admin/jobs/extractions/:runId/retry",
    roles: ["superuser", "operations", "support"],
    clinical: false,
  },
  adminPharmacyOrders: {
    method: "GET",
    path: "/api/admin/pharmacy-orders",
    roles: OPS,
    clinical: false,
  },
  adminPharmacyOrder: {
    method: "GET",
    path: "/api/admin/pharmacy-orders/:pharmacyOrderId",
    roles: OPS,
    clinical: false,
  },
  adminPharmacyStatus: {
    method: "POST",
    path: "/api/admin/pharmacy-orders/:pharmacyOrderId/status",
    roles: OPS,
    clinical: false,
  },
  adminPharmacyResubmit: {
    method: "POST",
    path: "/api/admin/pharmacy-orders/:pharmacyOrderId/resubmit",
    roles: OPS,
    clinical: false,
  },
  adminPharmacyCancel: {
    method: "POST",
    path: "/api/admin/pharmacy-orders/:pharmacyOrderId/cancel",
    roles: OPS,
    clinical: false,
  },
  adminPharmacyComplete: {
    method: "POST",
    path: "/api/admin/pharmacy-orders/:pharmacyOrderId/complete",
    roles: OPS,
    clinical: false,
  },
  adminBilling: {
    method: "GET",
    path: "/api/admin/users/:userId/billing",
    roles: OPS,
    clinical: false,
  },
  adminPause: {
    method: "POST",
    path: "/api/admin/users/:userId/pause",
    roles: OPS,
    clinical: false,
  },
  adminCancelSubscription: {
    method: "POST",
    path: "/api/admin/users/:userId/cancel-subscription",
    roles: OPS,
    clinical: false,
  },
  adminRefund: {
    method: "POST",
    path: "/api/admin/orders/:orderId/refund",
    roles: OPS,
    clinical: false,
  },
  /* SUP-004. A customer reports a suspected reaction against their own patient record. */
  reactionReport: {
    method: "POST",
    path: "/api/reaction-reports",
    roles: ["customer"],
    clinical: true,
  },
  /* SUP-004. Unresolved reports, oldest first, so a clinician works the queue in order. */
  adminReactionReports: {
    method: "GET",
    path: "/api/admin/reaction-reports",
    roles: ACCOUNT_READERS,
    clinical: true,
  },
  adminResolveReaction: {
    method: "POST",
    path: "/api/admin/reaction-reports/:reportId/resolve",
    roles: ACCOUNT_READERS,
    clinical: true,
  },
  /* ADM-030. Ingredients, forms, dose limits, pharmacy and whether they blend. */
  adminIngredients: {
    method: "GET",
    path: "/api/admin/ingredients",
    roles: ENGINE_STAFF,
    clinical: false,
  },
  adminCreateIngredient: {
    method: "POST",
    path: "/api/admin/ingredients",
    roles: ENGINE_STAFF,
    clinical: false,
  },
  adminUpdateIngredient: {
    method: "PUT",
    path: "/api/admin/ingredients/:ingredientId",
    roles: ENGINE_STAFF,
    clinical: false,
  },
  /* ADM-031 and ADM-034. Rule sets are data; a save is a candidate until promoted. */
  adminEngineVersions: {
    method: "GET",
    path: "/api/admin/engine-versions",
    roles: ENGINE_STAFF,
    clinical: false,
  },
  adminEngineVersion: {
    method: "GET",
    path: "/api/admin/engine-versions/:versionId",
    roles: ENGINE_STAFF,
    clinical: false,
  },
  adminCreateRuleSet: {
    method: "POST",
    path: "/api/admin/rule-sets",
    roles: ENGINE_STAFF,
    clinical: false,
  },
  /* ADM-032. Runs a candidate over the evaluation set and returns generated vs approved. */
  adminEvaluate: {
    method: "POST",
    path: "/api/admin/engine-versions/:versionId/evaluate",
    roles: ENGINE_STAFF,
    clinical: false,
  },
  adminPromote: {
    method: "POST",
    path: "/api/admin/engine-versions/:versionId/promote",
    roles: ENGINE_STAFF,
    clinical: false,
  },
  adminRollback: {
    method: "POST",
    path: "/api/admin/engine-versions/:versionId/rollback",
    roles: ENGINE_STAFF,
    clinical: false,
  },
  /* ADM-033. De-identified cases only. */
  adminEvaluation: {
    method: "GET",
    path: "/api/admin/evaluation",
    roles: ENGINE_STAFF,
    clinical: false,
  },
  adminRateEvaluation: {
    method: "POST",
    path: "/api/admin/evaluation/rate",
    roles: ENGINE_STAFF,
    clinical: false,
  },
  /*
    ADM-050. Staff actions and clinical record access: who, action, target, reason, when. Filters
    in `?actor=`, `?patient=`, `?from=` and `?to=`. Not clinical: the log never returns lab values.
  */
  adminAudit: {
    method: "GET",
    path: "/api/admin/audit",
    roles: ACCOUNT_READERS,
    clinical: false,
  },
  /* ADM-012. Takes `{ reason }`. Records who verified the licence and when. */
  adminVerifyLicence: {
    method: "POST",
    path: "/api/admin/users/:userId/licence/verify",
    roles: ["superuser", "operations", "support"],
    clinical: false,
  },
  clinicSignIn: {
    method: "POST",
    path: "/api/clinic/sign-in",
    roles: ["anyone"],
    clinical: false,
  },
  clinicGet: {
    method: "GET",
    path: "/api/clinic",
    roles: ["signed_in"],
    clinical: false,
  },
  clinicRegister: {
    method: "POST",
    path: "/api/clinic",
    roles: ["signed_in"],
    clinical: false,
  },
  clinicInvite: {
    method: "POST",
    path: "/api/clinic/invitations",
    roles: ["admin"],
    clinical: false,
  },
  clinicAccept: {
    method: "POST",
    path: "/api/clinic/invitations/accept",
    roles: ["signed_in"],
    clinical: false,
  },
  clinicLicence: {
    method: "POST",
    path: "/api/clinic/licence",
    roles: ["practitioner"],
    clinical: false,
  },
  clinicUpdateMember: {
    method: "POST",
    path: "/api/clinic/members/:userId",
    roles: ["admin"],
    clinical: false,
  },
  clinicRemoveMember: {
    method: "DELETE",
    path: "/api/clinic/members/:userId",
    roles: ["admin"],
    clinical: false,
  },
  clinicAssign: {
    method: "POST",
    path: "/api/clinic/links/:linkId/assign",
    roles: ["admin"],
    clinical: false,
  },
  /* PRA-010 to PRA-014. Clinic staff add patients, upload labs, assign, and end a link. */
  clinicPatients: {
    method: "GET",
    path: "/api/clinic/patients",
    roles: ["admin", "practitioner", "assistant"],
    clinical: false,
  },
  clinicAddPatient: {
    method: "POST",
    path: "/api/clinic/patients",
    roles: ["practitioner", "assistant"],
    clinical: true,
  },
  clinicPatient: {
    method: "GET",
    path: "/api/clinic/patients/:patientId",
    roles: ["practitioner", "assistant"],
    clinical: true,
  },
  clinicPatientUpload: {
    method: "POST",
    path: "/api/clinic/patients/:patientId/uploads",
    roles: ["practitioner", "assistant"],
    clinical: true,
  },
  clinicAssignPatient: {
    method: "POST",
    path: "/api/clinic/patients/:patientId/assign",
    roles: ["admin"],
    clinical: false,
  },
  clinicRemovePatient: {
    method: "POST",
    path: "/api/clinic/patients/:patientId/remove",
    roles: ["admin", "practitioner"],
    clinical: false,
  },
  /* PRA-013 / MSG-031. The customer named in the emailed token reviews the clinic's request. */
  clinicLinkRequest: {
    method: "GET",
    path: "/api/clinic/link-requests/:token",
    roles: ["customer"],
    clinical: true,
  },
  clinicLinkAccept: {
    method: "POST",
    path: "/api/clinic/link-requests/:token/accept",
    roles: ["customer"],
    clinical: true,
  },
  clinicLinkDecline: {
    method: "POST",
    path: "/api/clinic/link-requests/:token/decline",
    roles: ["customer"],
    clinical: true,
  },
  /* PRA-020 to PRA-023. Practitioner draft, edits, submit, and physician-change diffs. */
  clinicFormula: {
    method: "GET",
    path: "/api/clinic/patients/:patientId/formula",
    roles: ["practitioner"],
    clinical: true,
  },
  clinicFormulaIntake: {
    method: "POST",
    path: "/api/clinic/patients/:patientId/formula/intake",
    roles: ["practitioner"],
    clinical: true,
  },
  clinicFormulaDraft: {
    method: "POST",
    path: "/api/clinic/patients/:patientId/formula",
    roles: ["practitioner"],
    clinical: true,
  },
  clinicFormulaEdit: {
    method: "POST",
    path: "/api/clinic/patients/:patientId/formula/edits",
    roles: ["practitioner"],
    clinical: true,
  },
  clinicFormulaSubmit: {
    method: "POST",
    path: "/api/clinic/patients/:patientId/formula/submit",
    roles: ["practitioner"],
    clinical: true,
  },
  clinicFormulaChanges: {
    method: "GET",
    path: "/api/clinic/patients/:patientId/formula/changes",
    roles: ["practitioner"],
    clinical: true,
  },
  clinicFormulaAcknowledge: {
    method: "POST",
    path: "/api/clinic/patients/:patientId/formula/changes/acknowledge",
    roles: ["practitioner"],
    clinical: true,
  },
} as const satisfies Readonly<Record<string, RouteSpec>>;

export type RouteId = keyof typeof ROUTES;

/**
 * True for the staff surface. AUT-031 used to require Cloudflare Access on every one of these
 * paths. A staff session is enough to open them so password login on admin.latticenutrition.com
 * works before Access is set up. Signed-out callers are still refused.
 */
export function isStaffSurface(path: string): boolean {
  return path.startsWith("/api/admin/") || path.startsWith("/api/staff/");
}

/** A concrete path from a pattern: "/api/admin/users/:userId" with "u 1" is "/api/admin/users/u%201". */
export function pathOf(pattern: string, params: Readonly<Record<string, string>>): string {
  return pattern
    .split("/")
    .map((part) => (part.startsWith(":") ? encodeURIComponent(params[part.slice(1)] ?? "") : part))
    .join("/");
}

/**
 * The parameters a path matches, or null. "/api/patients/:patientId" matches "/api/patients/p1".
 * A trailing "*" matches one or more remaining segments.
 */
export function matchPath(pattern: string, path: string): Readonly<Record<string, string>> | null {
  const want = pattern.split("/");
  const got = path.split("/");

  if (want.at(-1) === "*") {
    const prefix = want.slice(0, -1);

    return got.length > prefix.length && prefix.every((part, i) => part === got[i]) ? {} : null;
  }

  if (want.length !== got.length) return null;
  const params: Record<string, string> = {};

  for (const [index, part] of want.entries()) {
    const actual = got[index] ?? "";

    if (part.startsWith(":")) params[part.slice(1)] = decodeURIComponent(actual);
    else if (part !== actual) return null;
  }

  return params;
}
